import { defineStore } from 'pinia'
import secureStorage from '@/utils/secureStorage'

export const useUserStore = defineStore('user', {
  // 状态定义
  state: () => ({
    // 用户信息
    userInfo: {
      id: null,
      username: '',
      displayName: '',
      role: '', // 统一为后端角色：ROLE_ADMIN / ROLE_AFTER_SALES / ROLE_TECHNICIAN / ROLE_GUEST
      avatar: ''
    },
    
    // 登录状态
    isLoggedIn: false,
    
    // 认证令牌（读取加密存储，兼容旧明文）
    token: secureStorage.get('token') || null,
    
    // 权限列表
    permissions: []
  }),

  // 计算属性 (类似 computed)
  getters: {
    // 是否是管理员
    isAdmin: (state) => state.userInfo.role === 'ROLE_ADMIN',
    
    // 是否是维修工
    isFixer: (state) => state.userInfo.role === 'ROLE_TECHNICIAN',

    // 是否是售后
    isAfterSales: (state) => state.userInfo.role === 'ROLE_AFTER_SALES',
    
    // 用户显示名称
    displayName: (state) => state.userInfo.displayName || state.userInfo.username || '未登录用户',
    
    // 是否有特定权限
    hasPermission: (state) => (permission) => {
      return state.permissions.includes(permission)
    }
  },

  // 方法 (类似 methods)
  actions: {
    // 登录
    async login(credentials) {
      try {
        // 调用真实登录API
        const { AuthAPI } = await import('@/request/login')
        const res = await AuthAPI.signin({
          username: credentials.username,
          password: credentials.password
        })

        // 后端返回的数据结构包含：token, type, id, username, displayName, roles
        const roles = Array.isArray(res.roles) ? res.roles : []
        // 选择主要角色：优先 ADMIN，其次 AFTER_SALES，再其次 TECHNICIAN，否则 GUEST
        const primaryRole = roles.includes('ROLE_ADMIN')
          ? 'ROLE_ADMIN'
          : roles.includes('ROLE_AFTER_SALES')
            ? 'ROLE_AFTER_SALES'
            : roles.includes('ROLE_TECHNICIAN')
            ? 'ROLE_TECHNICIAN'
            : roles.includes('ROLE_GUEST')
              ? 'ROLE_GUEST'
              : ''

        const user = {
          id: res.id ?? null,
          username: res.username ?? credentials.username,
          displayName: res.displayName ?? res.username ?? credentials.username,
          role: primaryRole,
          avatar: ''
        }

        // 更新状态
        this.userInfo = user
        this.token = res.token
        this.permissions = roles // 简化为后端角色列表
        this.isLoggedIn = true

        // 保存到本地存储（加密）
        secureStorage.set('token', res.token)
        secureStorage.set('userInfo', user)

        return { success: true, user }
      } catch (error) {
        console.error('登录失败:', error)
        return { success: false, error: error.message }
      }
    },

    // 登出
    logout() {
      // 清空状态
      this.userInfo = {
        id: null,
        username: '',
        displayName: '',
        role: '',
        avatar: ''
      }
      this.token = null
      this.permissions = []
      this.isLoggedIn = false
      
      // 清空本地存储（加密存储）
      secureStorage.remove('token')
      secureStorage.remove('userInfo')
    },

    // 初始化用户信息（应用启动时调用）
    initializeAuth() {
      // 优先从加密存储读取；若为空则兼容旧明文并迁移
      let token = secureStorage.get('token')
      let userInfo = secureStorage.get('userInfo')

      // 兼容旧明文数据：若加密读不到但明文存在，则迁移为加密
      if (!token) {
        const oldToken = localStorage.getItem('token')
        if (oldToken) {
          secureStorage.set('token', oldToken)
          token = oldToken
        }
      }
      if (!userInfo) {
        const oldUserInfo = localStorage.getItem('userInfo')
        if (oldUserInfo) {
          try {
            const parsed = JSON.parse(oldUserInfo)
            secureStorage.set('userInfo', parsed)
            userInfo = parsed
          } catch {
            secureStorage.set('userInfo', oldUserInfo)
            userInfo = oldUserInfo
          }
        }
      }
      
      if (token && userInfo) {
        try {
          this.token = token
          const parsed = typeof userInfo === 'string' ? JSON.parse(userInfo) : userInfo
          // 兼容旧数据角色（'admin'/'fixer'）迁移为后端标准角色
          if (parsed && parsed.role) {
            if (parsed.role === 'admin') parsed.role = 'ROLE_ADMIN'
            else if (parsed.role === 'aftersales') parsed.role = 'ROLE_AFTER_SALES'
            else if (parsed.role === 'fixer') parsed.role = 'ROLE_TECHNICIAN'
          }
          this.userInfo = parsed
          this.isLoggedIn = true
          
          // 这里可以验证token是否有效
          // this.validateToken()
          // 若旧数据中仍存在已停用的 user 角色，则清除
          if (this.userInfo && this.userInfo.role === 'user') {
            this.logout()
          }
        } catch (error) {
          console.error('初始化认证信息失败:', error)
          this.logout()
        }
      }
    },

    // 更新用户信息
    updateUserInfo(newInfo) {
      this.userInfo = { ...this.userInfo, ...newInfo }
      localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
    }
  }
})
